import React, { useState } from "react";

// region Libraries
import { Tab, Tabs } from "@material-ui/core";
import { useSelector } from "react-redux";
// endregion Libraries
// region Languages
import useTranslation from "src/translations/useTranslation";
import { CanConsumptionMessages } from "@shared/languages/interfaces";
// endregion Languages
// region Hooks
import usePersistedState from "@hooks/usePersistedState";
// endregion Hooks
// region Store
import { ScreenPlatform } from "@store/ducks/Screen/screen.type";
// endregion Store
// region Components
import Header from "@components/Header";
import TabPanel from "@components/TabPanel";
import CanConsumptionDashboard from "@pages/Reports/CanConsumption/Dashboard";
import VehiclesCanHistory from "@pages/Reports/VehiclesCanHistory";
import CanHistory from "@pages/Reports/CanHistory";
import CanGraph from "@pages/Reports/CanGraph";
// endregion Components
// region Styles
import { DefaultTheme } from "styled-components";
import light from "@styles/themes/light.theme";
import { Container } from "./styles";
// endregion Styles

/**
 * Single entry point of the CAN telemetry area: the dashboard plus the three CAN screens that used
 * to be separate menu items. All of them keep their own routes, so any saved link still works.
 *
 * The tabs are ordered as a funnel that narrows: the panel answers for the whole fleet over a
 * period, the synthetic report drops to one row per vehicle, the analytic report to one row per
 * message, and the graph closes it by plotting a single vehicle over a single day — which is where
 * you land once the analytic report has told you which vehicle and which day to look at.
 */
const CanConsumption: React.FC = () => {

  /* region States */
  const [selectedTab, setSelectedTab] = useState(0);
  /* endregion States */
  /* region Hooks */
  const { screen } = useSelector((screen) => screen);
  const [theme] = usePersistedState<DefaultTheme>("@Fleet:theme", light);
  const { t } = useTranslation();
  /* endregion Hooks */

  return (
    <>
      <Header title={t(CanConsumptionMessages.title)} />
      <Container platform={screen.platform as ScreenPlatform} className="page">
        <Tabs
          value={selectedTab}
          onChange={(_event, tab) => setSelectedTab(tab)}
          className="tabContainer"
          TabIndicatorProps={{ style: { backgroundColor: theme.colors.primary } }}
        >
          <Tab label={t(CanConsumptionMessages.tabDashboard)} disableRipple />
          <Tab label={t(CanConsumptionMessages.tabSyntheticReport)} disableRipple />
          <Tab label={t(CanConsumptionMessages.tabAnalyticReport)} disableRipple />
          <Tab label={t(CanConsumptionMessages.tabGraph)} disableRipple />
        </Tabs>
        <TabPanel value={selectedTab} index={0}>
          <CanConsumptionDashboard />
        </TabPanel>
        <TabPanel value={selectedTab} index={1}>
          <VehiclesCanHistory embedded />
        </TabPanel>
        <TabPanel value={selectedTab} index={2}>
          <CanHistory embedded />
        </TabPanel>
        <TabPanel value={selectedTab} index={3}>
          <CanGraph embedded />
        </TabPanel>
      </Container>
    </>
  );
};

export default CanConsumption;
